import type {
    CommerceConfiguration,
    ResolvedCommerceConfiguration,
} from "@teqtank/commerce-contracts";
import type { CommerceTransport } from "@teqtank/commerce-transport";
import { CommerceError } from "../errors/commerce-error.js";
import { createDefaultCommerceTransport } from "../transport/create-default-commerce-transport.js";

const MAXIMUM_COMMERCE_KEY_LENGTH = 64;
const COMMERCE_KEY_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]{2,63}$/u;
const CONTROL_CHARACTER_PATTERN = /[\u0000-\u001F\u007F]/u;
const DEFAULT_PRODUCT_DETAIL_ROUTE = "/products/{identifier}";
const DEFAULT_PRODUCT_DETAIL_QUERY_PARAMETER = "product";

export interface ResolvedCommerceInitialization {
    readonly configuration: ResolvedCommerceConfiguration;
    readonly transport: CommerceTransport;
    readonly paymentAdapter?: CommerceConfiguration["paymentAdapter"];
    readonly operationMiddleware: NonNullable<CommerceConfiguration["operationMiddleware"]>;
    readonly shareProviders: NonNullable<CommerceConfiguration["shareProviders"]>;
    readonly eventListeners: NonNullable<CommerceConfiguration["eventListeners"]>;
}

export function resolveCommerceConfiguration(
    configuration: CommerceConfiguration | null | undefined,
): ResolvedCommerceInitialization {
    if (configuration === null || configuration === undefined) {
        throw new CommerceError("configuration-required", "Commerce configuration is required.");
    }

    if (typeof configuration.commerceKey !== "string") {
        throw new CommerceError(
            "configuration-invalid",
            "Commerce configuration is invalid: commerceKey must be a string.",
            { property: "commerceKey" },
        );
    }

    const commerceKey = configuration.commerceKey.trim();

    if (commerceKey.length === 0) {
        throw new CommerceError(
            "configuration-invalid",
            "Commerce configuration is invalid: commerceKey cannot be empty.",
            { property: "commerceKey" },
        );
    }

    if (commerceKey.length > MAXIMUM_COMMERCE_KEY_LENGTH) {
        throw new CommerceError(
            "configuration-invalid",
            `Commerce configuration is invalid: commerceKey cannot exceed ${MAXIMUM_COMMERCE_KEY_LENGTH} characters.`,
            {
                property: "commerceKey",
                maximumLength: MAXIMUM_COMMERCE_KEY_LENGTH,
            },
        );
    }

    if (!COMMERCE_KEY_PATTERN.test(commerceKey)) {
        throw new CommerceError(
            "configuration-invalid",
            "Commerce configuration is invalid: commerceKey must be a URL-safe identifier.",
            { property: "commerceKey" },
        );
    }

    if (configuration.dataSource !== "playground" && configuration.dataSource !== "connected") {
        throw new CommerceError(
            "configuration-invalid",
            "Commerce configuration is invalid: dataSource must be 'playground' or 'connected'.",
            { property: "dataSource" },
        );
    }

    const transport = configuration.transport ?? createDefaultCommerceTransport();

    if (
        typeof transport !== "object" ||
        transport === null ||
        typeof transport.name !== "string" ||
        transport.name.trim().length === 0 ||
        typeof transport.execute !== "function"
    ) {
        throw new CommerceError(
            "configuration-invalid",
            "Commerce configuration is invalid: transport must implement CommerceTransport.",
            { property: "transport" },
        );
    }

    const operationMiddleware = validateFunctionArray(
        configuration.operationMiddleware,
        "operationMiddleware",
    );
    const eventListeners = validateFunctionArray(configuration.eventListeners, "eventListeners");
    const shareProviders = Object.freeze([...(configuration.shareProviders ?? [])]);
    for (const provider of shareProviders) {
        if (
            typeof provider !== "object" ||
            provider === null ||
            typeof provider.channel !== "string" ||
            provider.channel.trim().length === 0 ||
            typeof provider.label !== "string" ||
            provider.label.trim().length === 0 ||
            typeof provider.createUrl !== "function"
        ) {
            throw new CommerceError(
                "configuration-invalid",
                "Commerce configuration is invalid: each share provider must implement CommerceShareProvider.",
                { property: "shareProviders" },
            );
        }
    }

    const productDetail = resolveProductDetailConfiguration(configuration.productDetail);
    const storefront = resolveStorefrontConfiguration(configuration.storefront);

    return Object.freeze({
        configuration: Object.freeze({
            commerceKey,
            dataSource: configuration.dataSource,
            productDetail,
            storefront,
        }),
        transport,
        operationMiddleware,
        shareProviders,
        eventListeners,
        ...(configuration.paymentAdapter === undefined
            ? {}
            : { paymentAdapter: configuration.paymentAdapter }),
    });
}

function resolveStorefrontConfiguration(
    configuration: CommerceConfiguration["storefront"],
): ResolvedCommerceConfiguration["storefront"] {
    const resolveAlias = configuration?.resolveAlias ?? (() => null);
    if (typeof resolveAlias !== "function") {
        throw new CommerceError(
            "configuration-invalid",
            "Commerce storefront resolveAlias must be a function.",
            { property: "storefront.resolveAlias" },
        );
    }
    return Object.freeze({ resolveAlias });
}

function validateFunctionArray<TFunction>(
    value: readonly TFunction[] | undefined,
    property: string,
): readonly TFunction[] {
    const items = Object.freeze([...(value ?? [])]);
    if (items.some((item) => typeof item !== "function")) {
        throw new CommerceError(
            "configuration-invalid",
            `Commerce configuration is invalid: ${property} must contain functions.`,
            { property },
        );
    }
    return items;
}

function resolveProductDetailConfiguration(
    configuration: CommerceConfiguration["productDetail"],
): ResolvedCommerceConfiguration["productDetail"] {
    const identity = configuration?.identity ?? "slug";
    const routeTemplate = configuration?.routeTemplate?.trim() || DEFAULT_PRODUCT_DETAIL_ROUTE;
    const queryParameter =
        configuration?.queryParameter?.trim() || DEFAULT_PRODUCT_DETAIL_QUERY_PARAMETER;

    if (!(["slug", "id", "sku"] as const).includes(identity)) {
        throw new CommerceError(
            "configuration-invalid",
            "Commerce product detail identity must be 'slug', 'id' or 'sku'.",
            { property: "productDetail.identity" },
        );
    }

    if (CONTROL_CHARACTER_PATTERN.test(routeTemplate) || routeTemplate.length > 500) {
        throw new CommerceError(
            "configuration-invalid",
            "Commerce product detail routeTemplate is invalid.",
            { property: "productDetail.routeTemplate" },
        );
    }

    if (CONTROL_CHARACTER_PATTERN.test(queryParameter) || queryParameter.length > 100) {
        throw new CommerceError(
            "configuration-invalid",
            "Commerce product detail queryParameter is invalid.",
            { property: "productDetail.queryParameter" },
        );
    }

    return Object.freeze({ identity, routeTemplate, queryParameter });
}
